// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing's console — the engine's health as tiles and warnings, and each run's times,
// progress, requester and cost in plain words.

import type { RoutingConsoleJob, RoutingHealth, RoutingLive, RoutingOrigin } from '../../services/api';
import { modelName, type Tone } from './routingFormat';

const MIN = 60_000;
const H = 60 * MIN;
/** A run waiting longer than this to start is worth a look. */
const LONG_WAIT_MS = 10 * MIN;
/** A forecast older than this has missed at least one run. */
const OLD_FORECAST_MS = 24 * H;

/** 42 s · 6 m 12 s · 2 h 5 m */
export function clock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} m ${String(s % 60).padStart(2, '0')} s`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} m`;
}

export const isOpen = (j: Pick<RoutingConsoleJob, 'state'>) => j.state === 'queued' || j.state === 'running';

/** How long a run waited to start and how long it has worked, up to `now` while it goes on. */
export function runTimes(j: RoutingConsoleJob, now: number): { waitMs: number; runMs: number; totalMs: number } {
  const end = j.finishedMs ?? (isOpen(j) ? now : j.submittedMs);
  if (j.state === 'queued') return { waitMs: now - j.submittedMs, runMs: 0, totalMs: now - j.submittedMs };
  // Finished before anyone looked: the start was never seen, so it all counts as work.
  if (!j.startedMs) return { waitMs: 0, runMs: end - j.submittedMs, totalMs: end - j.submittedMs };
  return { waitMs: j.startedMs - j.submittedMs, runMs: end - j.startedMs, totalMs: end - j.submittedMs };
}

/** The engine's progress as a bar fraction and words, or null when it gives none. */
export function progressView(live: RoutingLive | undefined): { fraction: number; text: string } | null {
  const p = live?.progress;
  const eta = live?.etaSec;
  const left = typeof eta === 'number' ? (eta < 60 ? ' · under a minute left' : ` · about ${Math.round(eta / 60)} m left`) : '';
  if (typeof p === 'number') return { fraction: p, text: `${Math.round(p * 100)} %${left}` };
  if (p && p.total > 0) return { fraction: p.done / p.total, text: `${p.done} of ${p.total} ${p.unit}${left}` };
  return null;
}

const SOURCE: Record<string, string> = { admin: 'Admin', skipper: 'Skipper', planner: 'Departure planner', check: 'Departure check' };

/** Where a run came from, the person and their boat: an admin by name, anyone else by email (their `who` is the boat). */
export function originView(o: RoutingOrigin | undefined): { from: string; who: string; boat: string | null } {
  if (!o) return { from: 'Unknown', who: 'Not recorded', boat: null };
  const admin = o.source === 'admin';
  return { from: SOURCE[o.source] ?? o.source, who: (admin ? o.who ?? o.email : o.email ?? o.who) ?? 'Not recorded', boat: admin ? null : (o.who ?? null) };
}

export function money(usd: number | undefined): string {
  if (usd === undefined) return '—';
  if (usd > 0 && usd < 0.01) return '<$0.01';
  return `$${usd.toFixed(2)}`;
}

export interface HealthTile { key: string; label: string; value: string; detail: string; tone: Tone }
export interface HealthAlert { tone: 'warn' | 'err'; text: string }

const AWAKE = new Set(['running', 'healthy']);
const RANK: Record<Tone, number> = { ok: 0, info: 0, mute: 0, warn: 1, err: 2 };
const lost = (j: RoutingConsoleJob) => j.state === 'failed' && /^lost /.test(j.error ?? '');
const stopped = (j: RoutingConsoleJob) => j.state === 'failed' && /^cancelled by /.test(j.error ?? '');

function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
}

/** The health panel: tiles, the warnings beneath them, and the worst level among them. */
export function healthView(h: RoutingHealth, jobs: RoutingConsoleJob[], now: number): { tiles: HealthTile[]; alerts: HealthAlert[]; level: Tone } {
  const alerts: HealthAlert[] = [];
  const awake = AWAKE.has(h.shared.state);
  if (h.shared.state === 'unknown') alerts.push({ tone: 'err', text: 'The shared engine’s state could not be read.' });

  // An admin stopping a run is not a fault; a run lost to a restart is the engine's.
  const failures = jobs.filter((j) => j.state === 'failed' && !stopped(j));
  const lostRuns = jobs.filter(lost);
  if (lostRuns.length) alerts.push({ tone: 'err', text: `${lostRuns.length} run${lostRuns.length > 1 ? 's were' : ' was'} lost when the engine restarted.` });
  const others = failures.filter((j) => !lost(j));
  if (others.length) {
    const counts = new Map<string, number>();
    for (const j of others) counts.set(j.error ?? 'no reason given', (counts.get(j.error ?? 'no reason given') ?? 0) + 1);
    const [top] = [...counts].sort((a, b) => b[1] - a[1]);
    alerts.push({ tone: 'warn', text: `${others.length} run${others.length > 1 ? 's' : ''} failed. Most often: “${top[0]}”.` });
  }

  const running = jobs.filter((j) => j.state === 'running');
  const waiting = jobs.filter((j) => j.state === 'queued');
  const oldestWait = Math.max(0, ...waiting.map((j) => now - j.submittedMs));
  if (oldestWait > LONG_WAIT_MS) alerts.push({ tone: 'warn', text: `A run has waited ${clock(oldestWait)} to start.` });
  const ownFull = h.ownInUse >= h.ownLimit && h.ownLimit > 0;
  if (ownFull) alerts.push({ tone: 'warn', text: `All ${h.ownLimit} own containers are in use: new runs someone waits for queue on the shared engine.` });

  const ages = h.forecasts.map((f) => ({ name: modelName(f.model), age: f.publishedAt ? now - Date.parse(f.publishedAt) : null }));
  for (const f of ages) if (f.age !== null && f.age > OLD_FORECAST_MS) alerts.push({ tone: 'warn', text: `The ${f.name} forecast is ${Math.round(f.age / H)} h old.` });
  if (h.forecastError) alerts.push({ tone: 'err', text: h.forecastError });

  const done = jobs.filter((j) => j.state === 'done').map((j) => runTimes(j, now).totalMs);
  const med = median(done);
  const failRate = jobs.length ? failures.length / jobs.length : 0;
  const tiles: HealthTile[] = [
    { key: 'engine', label: 'Shared engine', value: awake ? 'Awake' : h.shared.state === 'unknown' ? 'Unknown' : 'Asleep', detail: h.engineVersion, tone: h.shared.state === 'unknown' ? 'err' : 'ok' },
    { key: 'containers', label: 'Containers', value: `${(awake ? 1 : 0) + h.ownInUse} / ${h.maxInstances}`, detail: `Shared + ${h.ownInUse} of ${h.ownLimit} own`, tone: ownFull ? 'warn' : 'ok' },
    { key: 'running', label: 'Running', value: String(running.length), detail: running.length ? `Longest ${clock(Math.max(...running.map((j) => runTimes(j, now).runMs)))}` : 'Nothing running', tone: 'ok' },
    { key: 'waiting', label: 'Waiting', value: String(waiting.length), detail: waiting.length ? `Oldest ${clock(oldestWait)}` : 'Nothing waiting', tone: oldestWait > LONG_WAIT_MS ? 'warn' : 'ok' },
    { key: 'failed', label: 'Failed', value: `${failures.length} of ${jobs.length}`, detail: jobs.length ? `${Math.round(failRate * 100)} % · stops by an admin not counted` : 'No runs', tone: !failures.length ? 'ok' : failRate >= 0.1 ? 'err' : 'warn' },
    { key: 'median', label: 'Typical run', value: med === null ? '—' : clock(med), detail: `Median of ${done.length} finished`, tone: 'ok' },
    {
      key: 'forecasts', label: 'Forecasts', value: h.forecastError ? 'Unreadable' : `${h.forecasts.length} current`,
      detail: h.forecasts.map((f) => `${modelName(f.model)} ${f.runId.slice(-3)}`).join(' · ') || '—',
      tone: h.forecastError ? 'err' : ages.some((f) => f.age !== null && f.age > OLD_FORECAST_MS) ? 'warn' : 'ok',
    },
  ];
  const level = [...tiles.map((t) => t.tone), ...alerts.map((a) => a.tone)].reduce<Tone>((w, t) => (RANK[t] > RANK[w] ? t : w), 'ok');
  return { tiles, alerts, level };
}
